/**
 * Visitor checks that run on every page: bot user agents, browser
 * automation and the cached visitor decision.
 *
 * The IP and geo lookups live in ip_geo.js, loaded as the "ip-geo" chunk
 * only when something needs them: an IP exclusion list, explicit consent
 * with restricted regions, or a stored IP for Facebook CAPI. The async
 * helpers below (pmw.getIp(), pmw.getClientIp(), ...) load the chunk on
 * first call, so callers keep working unchanged. The sync helpers
 * pmw.isValidIpAddress() and pmw.isPrivateOrReservedIp() exist only once
 * the chunk has loaded; await pmw.loadIpGeoModule() before using them.
 *
 * @since 1.70.1 split from the IP and geo lookups
 */

(function (pmw, undefined) {

	let ipGeoModulePromise = null

	/**
	 * Load the IP and geo lookups once.
	 *
	 * A failed load is not cached, so a later call can retry.
	 *
	 * @returns {Promise<void>}
	 *
	 * @since 1.70.1
	 */
	pmw.loadIpGeoModule = () => {

		if (!ipGeoModulePromise) {
			ipGeoModulePromise = import(/* webpackChunkName: "ip-geo" */ "./ip_geo.js")
				.catch(error => {
					ipGeoModulePromise = null
					throw error
				})
		}

		return ipGeoModulePromise
	}

	/**
	 * Define an async helper that loads the chunk and then calls the real
	 * implementation, which the chunk assigns to pmw[name] over this stub.
	 *
	 * @param {string} name
	 */
	const defineLazyHelper = (name) => {

		const stub = async (...args) => {

			await pmw.loadIpGeoModule()

			// Never call ourselves again if the chunk did not provide the helper
			if (pmw[name] === stub) throw new Error(`pmw.${name}() is missing from the ip-geo chunk`)

			return pmw[name](...args)
		}

		pmw[name] = stub
	}

	[
		"getIp",
		"getClientIp",
		"getIpWithTimeout",
		"getIpFromAnyService",
		"getIpFromService",
		"getBrowserGeoFromExternalService",
	].forEach(defineLazyHelper)

	/**
	 * Get the browser geo location, from the cache when possible.
	 *
	 * The cache read stays here so that a restricted-regions shop, which
	 * calls this on every page, loads the chunk only once per session.
	 *
	 * @returns {Promise<{countryCode: string, regionCode?: string, region?: string}|null>}
	 */
	pmw.getBrowserGeo = async () => {

		let geoFromStorage = {}

		if (pmw.retrieveData("geoCountryCode")) geoFromStorage.countryCode = pmw.retrieveData("geoCountryCode")
		if (pmw.retrieveData("geoRegionCode")) geoFromStorage.regionCode = pmw.retrieveData("geoRegionCode")
		if (pmw.retrieveData("geoRegion")) geoFromStorage.region = pmw.retrieveData("geoRegion")

		if (geoFromStorage.countryCode) {
			pmw.console.log("Retrieved the browser geo location from the cache: ", geoFromStorage)
			return geoFromStorage
		}

		await pmw.loadIpGeoModule()

		return pmw.lookUpBrowserGeo()
	}

	/**
	 * Whether a visitor IP from an earlier page is stored.
	 *
	 * Facebook CAPI validates it synchronously with pmw.isValidIpAddress(),
	 * so main.js loads the chunk alongside the pixels when this is true.
	 *
	 * @returns {boolean}
	 *
	 * @since 1.70.1
	 */
	pmw.hasStoredVisitorIp = () => {
		return Boolean(pmw.retrieveData("client_ip") || pmw.retrieveData("ipv6") || pmw.retrieveData("ip"))
	}

	/**
	 * Check if the visitor is allowed to trigger tracking events.
	 * This checks both bot user-agent patterns and custom IP exclusion list.
	 *
	 * When the IP exclusion list is populated, this blocks:
	 * - Browser-initiated S2S events
	 * - Browser pixel firing (via pmw._ipExcluded flag)
	 * Server-side purchase events are handled separately in PHP.
	 *
	 * @returns {Promise<boolean>} True if the visitor is allowed
	 */
	pmw.isVisitorAllowed = async () => {

		// Check the cache if visitor is allowed
		if (pmw.retrieveData("visitorAllowed") !== null && pmw.retrieveData("visitorAllowed") !== undefined) {
			const allowed = !!JSON.parse(pmw.retrieveData("visitorAllowed"))
			pmw._ipExcluded = !allowed
			return allowed
		}

		// Check if the visitor is a self-identifying bot or an automated browser
		if (pmw.isBotUserAgent() || pmw.isAutomatedBrowser()) {
			pmw.storeData("visitorAllowed", false)
			pmw._ipExcluded = true
			return false
		}

		// Check if IP is on the exclusion list (if custom exclusions are defined)
		const ipExcludeList = pmwDataLayer?.general?.server_2_server?.ip_exclude_list
		if (Array.isArray(ipExcludeList) && ipExcludeList.length > 0) {
			
			let ip

			try {
				ip = await pmw.getIp()
			} catch (error) {
				// The ip-geo chunk failed to load. Allow the visitor, as an
				// unreachable IP service always did, but leave the decision
				// uncached so the next page tries again.
				pmw.console.error("Could not load the IP lookup for the IP exclusion list:", error)
				pmw._ipExcluded = false
				return true
			}

			if (pmw.isOnIpExclusionList(ip)) {
				pmw.storeData("visitorAllowed", false)
				pmw._ipExcluded = true
				pmw.console.log(`Visitor IP ${ip} is on the exclusion list, all tracking suppressed`)
				return false
			}
		}

		// If not a bot and not on IP exclusion list, visitor is allowed
		pmw.storeData("visitorAllowed", true)
		pmw._ipExcluded = false
		return true
	}

	/**
	 * Synchronous counterpart to isVisitorAllowed().
	 *
	 * Resolves the visitor-allowed decision without ever awaiting, for the cases
	 * that don't require an IP lookup: a cached decision, a bot user-agent, or no
	 * IP exclusion list configured. Returns null only when an IP exclusion list is
	 * set and the decision isn't cached yet, i.e. when an async IP lookup is
	 * genuinely required (typically just the first event in a session).
	 *
	 * Used on the navigation-event send path so navigator.sendBeacon is reached
	 * before the page unloads. Mirrors isVisitorAllowed() branch-for-branch, minus
	 * the awaited getIp() lookup.
	 *
	 * @returns {boolean|null} true/false when decidable synchronously, else null
	 */
	pmw.isVisitorAllowedSync = () => {

		// Check the cache if visitor is allowed
		if (pmw.retrieveData("visitorAllowed") !== null && pmw.retrieveData("visitorAllowed") !== undefined) {
			const allowed = !!JSON.parse(pmw.retrieveData("visitorAllowed"))
			pmw._ipExcluded = !allowed
			return allowed
		}

		// Check if the visitor is a self-identifying bot or an automated browser
		if (pmw.isBotUserAgent() || pmw.isAutomatedBrowser()) {
			pmw.storeData("visitorAllowed", false)
			pmw._ipExcluded = true
			return false
		}

		// If no IP exclusion list is defined, the decision needs no IP lookup
		const ipExcludeList = pmwDataLayer?.general?.server_2_server?.ip_exclude_list
		if (!Array.isArray(ipExcludeList) || ipExcludeList.length === 0) {
			pmw.storeData("visitorAllowed", true)
			pmw._ipExcluded = false
			return true
		}

		// An IP exclusion list is set but the decision isn't cached yet:
		// it requires an async IP lookup, so the sync path cannot decide.
		return null
	}

	/**
	 * Check if the visitor is NOT allowed to trigger server-to-server events
	 * This checks both bot user-agent patterns and custom IP exclusion list
	 *
	 * @returns {Promise<boolean>} True if the visitor should be blocked
	 */
	pmw.isVisitorNotAllowed = async () => {
		return await pmw.isVisitorAllowed() === false
	}

	/**
	 * Check if the current user agent matches known bot patterns
	 *
	 * @returns {boolean} True if the user agent is a bot
	 */
	pmw.isBotUserAgent = () => {
		const userAgent = navigator.userAgent || ''
		
		// If no user agent, assume it might be a bot
		if (!userAgent) {
			return true
		}

		const botPatterns = getBotPatterns()

		// Check if user agent matches any bot pattern (case-insensitive)
		for (const pattern of botPatterns) {
			if (userAgent.toLowerCase().includes(pattern.toLowerCase())) {
				return true
			}
		}

		return false
	}

	/**
	 * Check whether the page is being driven by browser automation.
	 *
	 * The user-agent list above only catches crawlers that identify
	 * themselves. A scraper running Puppeteer, Playwright or Selenium sends a
	 * perfectly ordinary Chrome user agent and sails straight through it,
	 * while generating tracking events indistinguishable from a real
	 * visitor's, polluting conversion data and, on the SweetCode Cloud proxy,
	 * consuming the shop's request quota.
	 *
	 * navigator.webdriver is the W3C-standardised signal for exactly this: the
	 * spec requires a conforming WebDriver implementation to set it to true.
	 * It is not a fingerprint or a heuristic, it costs nothing to read, and a
	 * real browser under a real person never reports true, so there is no
	 * false-positive risk for genuine visitors. A determined scraper can patch
	 * it out, so this raises the floor rather than closing the door; IP/CIDR
	 * exclusions remain the tool for ranges that mask themselves.
	 *
	 * https://w3c.github.io/webdriver/#interface
	 *
	 * @returns {boolean} True if the browser reports being automated
	 */
	pmw.isAutomatedBrowser = () => {
		return true === navigator?.webdriver
	}

	/**
	 * Get list of known bot user-agent patterns
	 *
	 * @returns {string[]} Array of bot patterns
	 */
	const getBotPatterns = () => {
		// Get custom patterns from pmwDataLayer if available
		let customPatterns = []
		if (Array.isArray(pmwDataLayer?.general?.server_2_server?.user_agent_exclude_patterns)) {
			customPatterns = pmwDataLayer.general.server_2_server.user_agent_exclude_patterns
		}

		// Default bot patterns - focusing on legitimate crawlers that identify themselves
		const defaultPatterns = [
			// Search engine crawlers
			'Googlebot',
			'Google-InspectionTool',
			'Google-Extended',
			'Storebot-Google',
			'bingbot',
			'BingPreview',
			'msnbot',
			'Slurp',
			'DuckDuckBot',
			'Baiduspider',
			'YandexBot',
			'Sogou',

			// The rest of Google's fleet. Google runs many crawlers whose token
			// is NOT "Googlebot", so none of the patterns above and none of the
			// generic ones below match them. GoogleOther in particular crawls
			// product pages for Merchant Center and was caught firing tracking
			// events on a live shop; AdsBot fetches landing pages to score ad
			// quality, so it hits exactly the pages a store advertises.
			// https://developers.google.com/search/docs/crawling-indexing/overview-google-crawlers
			'GoogleOther',
			'AdsBot-Google',
			'Mediapartners-Google',
			'APIs-Google',
			'FeedFetcher-Google',
			'Google-CloudVertexBot',
			'Google-Read-Aloud',
			'Google-Safety',
			'GoogleProducer',
			'Chrome-Lighthouse',

			// Other high-volume crawlers
			'Bytespider',
			'Amazonbot',
			'Applebot',
			'PetalBot',
			'DataForSeoBot',
			'BLEXBot',
			'Barkrowler',
			'ImagesiftBot',
			'SeekportBot',
			'MojeekBot',
			'YisouSpider',
			'Qwantify',
			'Diffbot',
			
			// Social media crawlers
			'facebookexternalhit',
			'FacebookBot',
			'LinkedInBot',
			'Twitterbot',
			'PinterestBot',
			'TikTokBot',
			'SnapchatAds',
			'DiscordBot',
			'InstagramBot',
			'WhatsApp',
			
			// SEO & Analytics
			'AhrefsBot',
			'SemrushBot',
			'MJ12bot',
			'DotBot',
			'rogerbot',
			
			// Monitoring services
			'UptimeRobot',
			'Pingdom',
			'StatusCake',
			'GTmetrix',
			
			// AI & Research
			'ClaudeBot',
			'GPTBot',
			'ChatGPT',
			'anthropic-ai',
			'CCBot',
			'PerplexityBot',
			'OAI-SearchBot',
			'Meta-ExternalAgent',
			'meta-externalfetcher',
			'cohere-ai',
			'Applebot-Extended',

			// Automation frameworks and HTTP clients. A browser driven by one
			// of these is not a visitor, and a bare HTTP client cannot be one
			// at all. Complements the navigator.webdriver check in
			// isAutomatedBrowser, which catches automation that hides its
			// user agent but can itself be patched out.
			'HeadlessChrome',
			'Headless',
			'Puppeteer',
			'Playwright',
			'Selenium',
			'PhantomJS',
			'python-requests',
			'aiohttp',
			'Scrapy',
			'Go-http-client',
			'okhttp',
			'libwww-perl',
			'curl/',
			'Wget/',

			// Generic patterns
			'crawler',
			'spider',
			'scraper',
			'bot/',
			'/bot',
			'bot-',
		]

		return defaultPatterns.concat(customPatterns)
	}

}(window.pmw = window.pmw || {}))
